import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { treatmentsApi, appointmentsApi, providers } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Booking = () => {
  const { toast } = useToast();
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    treatment: '',
    provider: '',
    date: '',
    time: '',
    name: '',
    email: '',
    phone: '',
    notes: '',
    financing: false
  });

  useEffect(() => {
    fetchTreatments();
  }, []);

  const fetchTreatments = async () => {
    try {
      setLoading(true);
      const data = await treatmentsApi.getAll();
      setTreatments(data);
    } catch (err) {
      console.error('Error fetching treatments:', err);
      toast({
        title: 'Error',
        description: 'Failed to load treatments. Please refresh.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await appointmentsApi.create({
        patientName: bookingData.name,
        patientEmail: bookingData.email,
        patientPhone: bookingData.phone,
        treatment: bookingData.treatment,
        provider: bookingData.provider,
        date: bookingData.date,
        time: bookingData.time,
        notes: bookingData.notes,
        financing: bookingData.financing
      });
      
      toast({
        title: 'Booking Confirmed!',
        description: `Your ${bookingData.treatment} appointment is scheduled for ${bookingData.date} at ${bookingData.time}. Check your email for confirmation.`
      });
      
      // Reset form
      setBookingData({
        treatment: '',
        provider: '',
        date: '',
        time: '',
        name: '',
        email: '',
        phone: '',
        notes: '',
        financing: false
      });
      setStep(1);
    } catch (err) {
      console.error('Booking error:', err);
      toast({
        title: 'Booking Failed',
        description: 'There was an error creating your appointment. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (step === 1 && bookingData.treatment) {
      setStep(2);
    } else if (step === 2 && bookingData.provider && bookingData.date && bookingData.time) {
      setStep(3);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Book Your Appointment
          </h1>
          <p className="text-xl text-gray-600">
            Select your treatment and schedule your visit in just a few steps
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    step >= s
                      ? 'bg-primary text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div
                    className={`w-16 h-1 transition-colors ${
                      step > s ? 'bg-primary' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-lg">Loading treatments...</span>
          </div>
        ) : (
        <Card className="shadow-lg">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Select Treatment */}
            {step === 1 && (
              <>
                <CardHeader>
                  <CardTitle className="text-2xl">Select Treatment</CardTitle>
                  <CardDescription>
                    Choose the treatment you'd like to book
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    {treatments.map((treatment) => (
                      <div
                        key={treatment.id}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                          bookingData.treatment === treatment.name
                            ? 'border-primary bg-secondary'
                            : 'border-gray-200 hover:border-primary'
                        }`}
                        onClick={() => setBookingData({ ...bookingData, treatment: treatment.name })}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-lg">{treatment.name}</h3>
                          <span className="text-sm text-gray-600">${treatment.price}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{treatment.description}</p>
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="h-4 w-4 mr-1" />
                          {treatment.duration} min
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={!bookingData.treatment}
                  >
                    Continue
                  </Button>
                </CardFooter>
              </>
            )}

            {/* Step 2: Select Date & Provider */}
            {step === 2 && (
              <>
                <CardHeader>
                  <CardTitle className="text-2xl">Select Date & Provider</CardTitle>
                  <CardDescription>
                    Choose your preferred date, time, and provider
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Provider Selection */}
                  <div>
                    <Label className="text-base font-semibold mb-4 block">Select Provider</Label>
                    <div className="grid md:grid-cols-2 gap-4">
                      {providers.map((provider) => (
                        <div
                          key={provider.id}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                            bookingData.provider === provider.name
                              ? 'border-primary bg-secondary'
                              : 'border-gray-200 hover:border-primary'
                          }`}
                          onClick={() => setBookingData({ ...bookingData, provider: provider.name })}
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src={provider.image}
                              alt={provider.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                            <div>
                              <h3 className="font-semibold">{provider.name}</h3>
                              <p className="text-sm text-gray-600">{provider.specialty}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Date Selection */}
                  <div>
                    <Label htmlFor="date" className="text-base font-semibold">Select Date</Label>
                    <Input
                      id="date"
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      value={bookingData.date}
                      onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                      className="mt-2"
                    />
                  </div>

                  {/* Time Selection */}
                  <div>
                    <Label className="text-base font-semibold mb-3 block">Select Time</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {timeSlots.map((time) => (
                        <Button
                          key={time}
                          type="button"
                          variant={bookingData.time === time ? 'default' : 'outline'}
                          onClick={() => setBookingData({ ...bookingData, time })}
                        >
                          {time}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={!bookingData.provider || !bookingData.date || !bookingData.time}
                  >
                    Continue
                  </Button>
                </CardFooter>
              </>
            )}

            {/* Step 3: Your Information */}
            {step === 3 && (
              <>
                <CardHeader>
                  <CardTitle className="text-2xl">Your Information</CardTitle>
                  <CardDescription>
                    Enter your contact details to complete the booking
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      value={bookingData.name}
                      onChange={(e) => setBookingData({ ...bookingData, name: e.target.value })}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={bookingData.email}
                      onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={bookingData.phone}
                      onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes">Special Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Any specific concerns or questions?"
                      value={bookingData.notes}
                      onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                      className="mt-2"
                      rows={3}
                    />
                  </div>

                  {/* Financing Option */}
                  <div className="bg-secondary border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="financing"
                        checked={bookingData.financing}
                        onChange={(e) => setBookingData({ ...bookingData, financing: e.target.checked })}
                        className="mt-1 mr-3"
                      />
                      <div>
                        <Label htmlFor="financing" className="cursor-pointer font-semibold">
                          I'm interested in financing options
                        </Label>
                        <p className="text-sm text-gray-600 mt-1">
                          Get instant approval with soft credit check. 0% APR available for qualified applicants.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(2)}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={!bookingData.name || !bookingData.email || !bookingData.phone || submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Booking...
                      </>
                    ) : (
                      'Confirm Booking'
                    )}
                  </Button>
                </CardFooter>
              </>
            )}
          </form>
        </Card>
        )}

        {/* Integration Note */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Powered by Boulevard Software Integration</p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Booking;